import type { Sortable, UniversallyUniqueId } from '../common'
import type { BLOCK_TYPE } from '../enums'

import type { BlockBaseSettings, IBlock } from './base'

export type ConsentItemId = UniversallyUniqueId

export enum CONSENT_TOPIC {
  GTC = 'GTC',
  DATA_PRIVACY = 'DATA_PRIVACY',
  CANCELLATION_POLICY = 'CANCELLATION_POLICY',
  EMAIL_MARKETING = 'EMAIL_MARKETING',
  SMS_MARKETING = 'SMS_MARKETING',
  PHONE_CALL_MARKETING = 'PHONE_CALL_MARKETING',
  PRINT_MARKETING = 'PRINT_MARKETING'
}

export interface ConsentItem extends Sortable {
  /**
   * Determines whether the user is obliged to give their consent.
   */
  required: boolean
  /**
   * Which topics this consent relates with.
   */
  topics: (CONSENT_TOPIC | string)[]
  /**
   * Render text in markdown format.
   */
  text?: string | null
}

export interface ConsentsBlock extends IBlock {
  type: BLOCK_TYPE.CONSENTS
  settings: BlockBaseSettings & {
    /** Map of consents */
    items: Record<ConsentItemId, ConsentItem>
  }
}

export type ConsentItemBlockValue = {
  /** Whether the end-user agreed */
  agreed: boolean
  /**
   * Which topic this consent is for.
   *
   * Warning: Different than the settings, this is a single topic.
   * If multiple topics are associated with a certain consent, multiple items will be created.
   *
   * @example "GTC"
   * @example "custom-topic"
   */
  topic: CONSENT_TOPIC | string
  /**
   * The provided text for the consent
   * TODO: can't we simply inter from the settings instead of repeating ourselves?
   *
   * @deprecated assuming we can eventually remove
   */
  text: string | null
  /**
   * When did the end-user agreed, according to {@link ConsentItemBlockValue.agreed}.
   *
   * Expected to be in ISO 8601 format.
   *
   * @example "2025-08-21T17:28:23.452Z"
   */
  time: Date
}

/**
 * Represents the value of a Consents block.
 *
 * The control stores a map of consent item ids to an object describing whether
 * the user agreed to the consent. The object may contain additional metadata
 * such as the timestamp of agreement.
 */
export type ConsentsBlockValue = Record<ConsentItemId, ConsentItemBlockValue>
